import React from 'react';
import { Form } from 'react-bootstrap';
import { MedicineDetailResult, Wholesale } from 'features/graphql';
import { isDatePassed, combineValueAndLabel, formatDate } from 'utils/dateUtils';
import { extractStopDates } from 'utils/stopDateUtils';
import { extractSaleStartDates } from 'utils/saleStartDateUtils';
import { PriceShowWholesaleCells } from './PriceShowWholesaleCells';

interface PriceShowMedicineRowProps {
  medicine: MedicineDetailResult;
  wholesales: Wholesale[];
  /** 「安」マーク列を表示するか（全医薬品にわたる判定のため親で算出して渡す） */
  showMinPriceColumn: boolean;
  zoomLevel: number;
  currentYear: number;
  previousYear: number;
  facilityStatus: string;
  /** アイコン列(fixed-col-3)の基準幅。親の columnWidths.fixedCol3 */
  fixedCol3Width: number;
  /** 見積追加チェックボックスの変更ハンドラ（選択状態は親が保持） */
  onAdd: (medicine: MedicineDetailResult, on: boolean) => void;
}

/**
 * 価格表の1医薬品分（5行1組の tbody）を描画する（D4 step7: PriceShowList の map 本体を分離）。
 * 状態は親が保持し props で受け渡す。行構造・条件・スタイルは分離前と不変。
 */
export const PriceShowMedicineRow: React.FC<PriceShowMedicineRowProps> = ({
  medicine,
  wholesales,
  showMinPriceColumn,
  zoomLevel,
  currentYear,
  previousYear,
  facilityStatus,
  fixedCol3Width,
  onAdd,
}) => {
  // 各medicine項目ごとに、stopExpectedDateから日付情報を抽出
  const stopDates = extractStopDates(medicine.stopExpectedDate);

  // sale_start_expected_date から日付情報を抽出
  const startDates = extractSaleStartDates(medicine.saleStartExpectedDate);

  const listingPlanDate = startDates.listingPlanDate;
  const saleStartPlanDate = startDates.saleStartPlanDate;
  const listingDate = startDates.listingDate;
  const saleStartDate = startDates.saleStartDate;

  // 各卸毎の価格がある場合をチェックする関数（1卸でも価格があれば true）
  const hasAnyPrice = () => {
    if (!medicine.deals || medicine.deals.length === 0) return false;
    return medicine.deals.some(
      (deal) => deal?.packagingPrice !== null && deal?.packagingPrice !== undefined
    );
  };

  // 行をグレーアウトする条件をチェック（経過措置期間終了または販売中止のみ）
  const isRowGreyedOut =
    isDatePassed(stopDates.expirationDate) || isDatePassed(stopDates.salesStopDate);

  // チェックボックスを無効にする条件をチェック（グレーアウト条件 + 価格がある場合）
  const isCheckboxDisabled = isRowGreyedOut || hasAnyPrice();

  // 共通のセルスタイル - これを各セルに適用する
  const cellBackgroundColor = isRowGreyedOut ? '#d6d6d6' : 'white';

  // 卸セル(5行)で共通のprops。行ごとに rowIndex のみ差し替える。
  const wholesaleCellProps = {
    medicine,
    cellBackgroundColor,
    wholesales,
    showMinPriceColumn,
    zoomLevel,
    previousYear,
    facilityStatus,
  };

  return (
    <tbody className={isRowGreyedOut ? 'disabled-row' : ''}>
      <tr>
        <td
          rowSpan={5}
          className="align-middle fixed-col-1"
          style={{
            backgroundColor: cellBackgroundColor,
          }}
        >
          <Form.Check
            onChange={(e) => onAdd(medicine, e.target.checked)}
            disabled={isCheckboxDisabled}
          />
        </td>
        <td
          rowSpan={5}
          className="align-middle fixed-col-2"
          style={{
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.medicineType}
        </td>
        <td
          className="fixed-col-3"
          style={{
            backgroundColor: cellBackgroundColor,
          }}
        ></td>
        <td
          className="fixed-col-4 fixed-column text-success"
          style={{
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.medicineName}
        </td>
        <td
          style={{
            width: `${6 * zoomLevel}rem`,
            minWidth: `${6 * zoomLevel}rem`,
            maxWidth: `${7 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {previousYear}年度
        </td>
        <td
          style={{
            width: `${6 * zoomLevel}rem`,
            minWidth: `${6 * zoomLevel}rem`,
            maxWidth: `${7 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {currentYear}年度
        </td>

        {/* 動的に生成される卸売業者データセル */}
        <PriceShowWholesaleCells {...wholesaleCellProps} rowIndex={1} />
        <td
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${10 * zoomLevel}rem`,
            maxWidth: `${10 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {combineValueAndLabel(listingPlanDate, '(薬価収載)')}
        </td>
        <td
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${10 * zoomLevel}rem`,
            maxWidth: `${10 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {combineValueAndLabel(stopDates.salesStopExpirationDate, '(経過措置)')}
        </td>
        <td
          style={{
            minWidth: `${10 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.janCode}
        </td>
      </tr>
      <tr>
        {medicine.generic === 1 ? (
          <td
            className="fixed-col-3 align-middle medicine-icon"
            style={{
              backgroundColor: cellBackgroundColor,
              padding: 0,
              verticalAlign: 'middle',
            }}
          >
            <div
              style={{
                display: 'flex', // flexboxを使用
                justifyContent: 'center', // 水平方向の中央揃え
                alignItems: 'center', // 垂直方向の中央揃え
                margin: '2px',
                border: '1px solid var(--bs-success)',
                borderRadius: '20px',
                color: 'var(--bs-success)',
                fontSize: '0.85em',
                width: '2.5rem',
                height: '1.5rem',
              }}
            >
              後発
            </div>
          </td>
        ) : (
          <td
            className="fixed-col-3"
            style={{
              width: `${fixedCol3Width * zoomLevel}rem`,
              minWidth: `${fixedCol3Width * zoomLevel}rem`,
              maxWidth: `${fixedCol3Width * zoomLevel}rem`,
              backgroundColor: cellBackgroundColor,
            }}
          ></td>
        )}
        <td
          className="fixed-col-4 fixed-column"
          style={{
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.capacity}
        </td>
        <td
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${8 * zoomLevel}rem`,
            maxWidth: `${10 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.previousStartDate && formatDate(medicine.previousStartDate)}
          {medicine.previousStartDate && medicine.previousEndDate && ' - '}
          {medicine.previousEndDate && formatDate(medicine.previousEndDate)}
        </td>
        <td
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${8 * zoomLevel}rem`,
            maxWidth: `${10 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.currentStartDate && formatDate(medicine.currentStartDate)}
          {medicine.currentStartDate && medicine.currentEndDate && ' - '}
          {medicine.currentEndDate && formatDate(medicine.currentEndDate)}
        </td>

        {/* 動的に生成される卸売業者データセル */}
        <PriceShowWholesaleCells {...wholesaleCellProps} rowIndex={2} />
        <td
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${10 * zoomLevel}rem`,
            maxWidth: `${10 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {combineValueAndLabel(saleStartPlanDate, '(販売開始)')}
        </td>
        <td
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${10 * zoomLevel}rem`,
            maxWidth: `${10 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {combineValueAndLabel(stopDates.salesStopDate, '(販売中止)')}
        </td>
        <td
          style={{
            minWidth: `${10 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.yjCode}
        </td>
      </tr>
      <tr>
        {medicine.authorizedGeneric === 1 ? (
          <td
            className="fixed-col-3 align-middle medicine-icon"
            style={{
              backgroundColor: cellBackgroundColor,
              padding: 0,
              verticalAlign: 'middle',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '2px',
                backgroundColor: 'var(--bs-success)',
                color: 'white',
                borderRadius: '20px',
                fontSize: '0.85em',
                width: '2.5rem',
                height: '1.5rem',
              }}
            >
              AG
            </div>
          </td>
        ) : (
          <td
            className="fixed-col-3"
            style={{
              backgroundColor: cellBackgroundColor,
            }}
          ></td>
        )}
        <td
          className="fixed-col-4 fixed-column"
          style={{
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.commonName}
        </td>
        <td
          style={{
            width: `${7 * zoomLevel}rem`,
            minWidth: `${5 * zoomLevel}rem`,
            maxWidth: `${7 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          旧薬価
        </td>
        <td
          style={{
            width: `${7 * zoomLevel}rem`,
            minWidth: `${5 * zoomLevel}rem`,
            maxWidth: `${7 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          <span className="text-success">新薬価</span>
        </td>

        {/* 動的に生成される卸売業者データセル */}
        <PriceShowWholesaleCells {...wholesaleCellProps} rowIndex={3} />
        <td colSpan={2} style={{ backgroundColor: cellBackgroundColor, width: 'auto' }}></td>
        <td
          style={{
            minWidth: `${10 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.gs1Code}
        </td>
      </tr>
      <tr>
        {medicine.biosimilar === 1 ? (
          <td
            className="fixed-col-3 align-middle medicine-icon"
            style={{
              backgroundColor: cellBackgroundColor,
              padding: 0,
              verticalAlign: 'middle',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '2px',
                backgroundColor: 'var(--bs-success)',
                color: 'white',
                borderRadius: '20px',
                fontSize: '0.85em',
                width: '2.5rem',
                height: '1.5rem',
              }}
            >
              BS
            </div>
          </td>
        ) : (
          <td
            className="fixed-col-3"
            style={{
              backgroundColor: cellBackgroundColor,
            }}
          ></td>
        )}
        <td
          className="fixed-col-4 fixed-column"
          style={{
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.drugCategoryName}
        </td>
        <td
          style={{
            width: `${7 * zoomLevel}rem`,
            minWidth: `${6 * zoomLevel}rem`,
            maxWidth: `${8 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.previousPrice?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </td>
        <td
          style={{
            width: `${7 * zoomLevel}rem`,
            minWidth: `${6 * zoomLevel}rem`,
            maxWidth: `${8 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
          className="text-success"
        >
          {medicine.currentPrice?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </td>

        {/* 動的に生成される卸売業者データセル */}
        <PriceShowWholesaleCells {...wholesaleCellProps} rowIndex={4} />
        <td
          style={{
            width: `${12 * zoomLevel}rem`,
            minWidth: `${12 * zoomLevel}rem`,
            maxWidth: `${12 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {listingDate}
        </td>
        <td
          style={{
            width: `${12 * zoomLevel}rem`,
            minWidth: `${12 * zoomLevel}rem`,
            maxWidth: `${12 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {stopDates.expirationDate}
        </td>
        <td style={{ backgroundColor: cellBackgroundColor, width: 'auto' }}></td>
      </tr>
      <tr className="border-bottom">
        <td
          className="fixed-col-3"
          style={{
            width: '3rem',
            minWidth: '3rem',
            backgroundColor: cellBackgroundColor,
            textAlign: 'center',
          }}
        ></td>
        <td
          className="fixed-col-4 fixed-column"
          style={{
            backgroundColor: cellBackgroundColor,
          }}
        >
          {medicine.salesManufacturer}
        </td>
        <td style={{ backgroundColor: cellBackgroundColor, width: 'auto' }}></td>
        <td style={{ backgroundColor: cellBackgroundColor, width: 'auto' }}></td>
        {/* 動的に生成される卸売業者データセル */}
        <PriceShowWholesaleCells {...wholesaleCellProps} rowIndex={5} />
        <td
          style={{
            width: `${12 * zoomLevel}rem`,
            minWidth: `${12 * zoomLevel}rem`,
            maxWidth: `${12 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {saleStartDate}
        </td>
        <td
          style={{
            width: `${12 * zoomLevel}rem`,
            minWidth: `${12 * zoomLevel}rem`,
            maxWidth: `${12 * zoomLevel}rem`,
            backgroundColor: cellBackgroundColor,
          }}
        >
          {stopDates.productionStopDate}
        </td>
        <td style={{ backgroundColor: cellBackgroundColor, width: 'auto' }}></td>
      </tr>
    </tbody>
  );
};
